import React, { useState } from 'react';
import { MagnifyingGlass, SlidersHorizontal, X, Check, CaretDown, ArrowsDownUp } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export const SORT_CHOICES = [
  { value: 'newest', label: 'Newest' },
  { value: 'return_desc', label: 'Highest projected return' },
  { value: 'hold_asc', label: 'Shortest duration' },
  { value: 'minimum_asc', label: 'Lowest minimum' },
  { value: 'funded_desc', label: 'Most funded' },
];

export const RETURN_BANDS = [
  { value: 'lt15', label: 'Under 15%' },
  { value: '15to25', label: '15% – 25%' },
  { value: 'gt25', label: 'Over 25%' },
];

export const DURATION_BANDS = [
  { value: 'lte12', label: 'Up to 12 months' },
  { value: '12to24', label: '12 – 24 months' },
  { value: 'gt24', label: 'Over 24 months' },
];

export const FUNDING_STATES = [
  { value: 'new', label: 'New' },
  { value: 'funding', label: 'Funding' },
  { value: 'fully_funded', label: 'Fully funded' },
  { value: 'coming_soon', label: 'Coming soon' },
  { value: 'closed', label: 'Closed' },
];

const FilterDropdown = ({ label, options, value, onChange, testId }) => {
  const active = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`inline-flex items-center gap-1.5 px-3.5 h-[35px] rounded-full text-[13px] border transition-colors ${
            active ? 'bg-primary/10 border-primary/30 text-primary font-medium' : 'bg-white border-black/[0.08] hover:border-black/20'
          }`}
          data-testid={testId}
        >
          {active ? active.label : label}
          <CaretDown size={13} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="rounded-2xl p-1.5 min-w-[220px]">
        {[{ value: '', label: `All ${label.toLowerCase()}` }, ...options].map((option) => (
          <DropdownMenuItem
            key={option.value || 'all'}
            onClick={() => onChange(option.value)}
            className="rounded-xl text-sm cursor-pointer flex items-center justify-between"
            data-testid={`${testId}-option-${option.value || 'all'}`}
          >
            {option.label}
            {value === option.value && <Check size={14} weight="bold" className="text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const SearchBar = ({ value, onChange }) => (
  <div className="relative flex-1 min-w-0">
    <MagnifyingGlass size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted" />
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Search projects..."
      aria-label="Search projects"
      className="w-full h-[35px] pl-10 pr-4 rounded-full bg-white border border-black/[0.08] text-[13px]
        placeholder:text-foreground-muted focus:outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/10"
      data-testid="marketplace-search-input"
    />
  </div>
);

export const SortSelector = ({ value, onChange, testId = 'marketplace-sort', compact = false }) => {
  const active = SORT_CHOICES.find((s) => s.value === value) || SORT_CHOICES[0];
  const sorted = value && value !== 'newest';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <button
            className={`inline-flex items-center justify-center w-[35px] h-[35px] rounded-full border flex-shrink-0 ${
              sorted ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-white border-black/[0.08]'
            }`}
            aria-label={`Sort: ${active.label}`}
            data-testid={testId}
          >
            <ArrowsDownUp size={16} weight="bold" />
          </button>
        ) : (
          <button
            className="inline-flex items-center gap-1.5 px-3.5 h-[35px] rounded-full text-[13px] bg-white border border-black/[0.08] hover:border-black/20 whitespace-nowrap"
            data-testid={testId}
          >
            <span className="text-foreground-muted hidden sm:inline">Sort:</span> {active.label}
            <CaretDown size={13} weight="bold" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-2xl p-1.5 min-w-[240px]">
        {SORT_CHOICES.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => onChange(option.value)}
            className="rounded-xl text-sm cursor-pointer flex items-center justify-between"
            data-testid={`${testId}-option-${option.value}`}
          >
            {option.label}
            {value === option.value && <Check size={14} weight="bold" className="text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ActiveFilterChips = ({ chips, onClear }) => {
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="active-filter-chips">
      {chips.map((chip) => (
        <button
          key={chip.key}
          onClick={chip.onRemove}
          className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/15"
          data-testid={`filter-chip-${chip.key}`}
        >
          {chip.label}
          <X size={12} weight="bold" />
        </button>
      ))}
      <button onClick={onClear} className="text-xs text-foreground-muted hover:text-foreground underline ml-1" data-testid="clear-all-filters">
        Clear all
      </button>
    </div>
  );
};

const SheetGroup = ({ title, options, value, onChange, testId }) => (
  <div className="py-5 border-b border-black/[0.06] last:border-0">
    <p className="text-[11px] uppercase tracking-[0.16em] text-foreground-muted mb-3">{title}</p>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            onClick={() => onChange(selected ? '' : option.value)}
            className={`px-3 h-[29px] rounded-full text-[13px] border transition-colors ${
              selected ? 'bg-primary text-white border-primary' : 'bg-white border-black/[0.1] hover:border-black/25'
            }`}
            data-testid={`${testId}-${option.value}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

export const MarketplaceToolbar = ({
  search,
  onSearch,
  filters,
  setFilter,
  clearFilters,
  sort,
  onSort,
  strategyOptions,
  locationOptions,
  resultCount,
  activeChips,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);

  return (
    <div data-testid="marketplace-toolbar">
      {/* Desktop toolbar */}
      <div className="hidden md:flex items-center gap-3">
        <div className="w-[320px]">
          <SearchBar value={search} onChange={onSearch} />
        </div>
        <FilterDropdown label="Strategy" options={strategyOptions} value={filters.strategy} onChange={(v) => setFilter('strategy', v)} testId="filter-strategy" />
        <FilterDropdown label="Location" options={locationOptions} value={filters.location} onChange={(v) => setFilter('location', v)} testId="filter-location" />
        <FilterDropdown label="Return" options={RETURN_BANDS} value={filters.returnBand} onChange={(v) => setFilter('returnBand', v)} testId="filter-return" />
        <FilterDropdown label="Duration" options={DURATION_BANDS} value={filters.durationBand} onChange={(v) => setFilter('durationBand', v)} testId="filter-duration" />
        <div className="ml-auto">
          <SortSelector value={sort} onChange={onSort} />
        </div>
      </div>

      {/* Mobile toolbar */}
      <div className="md:hidden flex items-center gap-2">
        <SearchBar value={search} onChange={onSearch} />
        <button
          onClick={() => setSheetOpen(true)}
          className={`relative inline-flex items-center justify-center w-[35px] h-[35px] rounded-full border flex-shrink-0 ${
            activeChips.length > 0 ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-white border-black/[0.08]'
          }`}
          aria-label="Filters"
          data-testid="open-filter-sheet"
        >
          <SlidersHorizontal size={16} weight="bold" />
          {activeChips.length > 0 && (
            <span className="absolute -top-1 -right-1 h-[18px] min-w-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-medium flex items-center justify-center">
              {activeChips.length}
            </span>
          )}
        </button>
        <SortSelector value={sort} onChange={onSort} testId="marketplace-sort-mobile" compact />
      </div>

      <ActiveFilterChips chips={activeChips} onClear={clearFilters} />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[88vh] overflow-y-auto px-5 pb-5" data-testid="filter-sheet">
          <SheetHeader className="text-left pb-1">
            <SheetTitle className="text-xl font-heading">Filters</SheetTitle>
          </SheetHeader>
          <SheetGroup title="Strategy" options={strategyOptions} value={filters.strategy} onChange={(v) => setFilter('strategy', v)} testId="sheet-strategy" />
          <SheetGroup title="Location" options={locationOptions} value={filters.location} onChange={(v) => setFilter('location', v)} testId="sheet-location" />
          <SheetGroup title="Projected return" options={RETURN_BANDS} value={filters.returnBand} onChange={(v) => setFilter('returnBand', v)} testId="sheet-return" />
          <SheetGroup title="Duration" options={DURATION_BANDS} value={filters.durationBand} onChange={(v) => setFilter('durationBand', v)} testId="sheet-duration" />
          <SheetGroup title="Funding status" options={FUNDING_STATES} value={filters.fundingStatus} onChange={(v) => setFilter('fundingStatus', v)} testId="sheet-funding" />
          <div className="flex items-center gap-3 pt-5 sticky bottom-0 bg-white">
            <Button variant="outline" className="rounded-full h-12 px-6 border-black/10" onClick={clearFilters} data-testid="sheet-clear-filters">
              Clear
            </Button>
            <Button
              className="flex-1 rounded-full h-12 bg-primary hover:bg-primary-hover text-white"
              onClick={() => setSheetOpen(false)}
              data-testid="sheet-apply-filters"
            >
              Show {resultCount} {resultCount === 1 ? 'opportunity' : 'opportunities'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export const EmptyState = ({ onClear }) => (
  <div className="py-20 text-center" data-testid="marketplace-empty-state">
    <h3 className="text-xl font-heading font-semibold">No opportunities match these filters.</h3>
    <p className="text-sm text-foreground-muted mt-2 max-w-md mx-auto">
      Try changing your filters or explore all current projects.
    </p>
    <Button onClick={onClear} className="mt-6 rounded-full h-11 px-6 bg-primary hover:bg-primary-hover text-white" data-testid="empty-clear-filters">
      Clear filters
    </Button>
  </div>
);
